import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDraftResult } from '@euler/model/bi/draft';
import {
  BiDeltaTrend,
  biDeltaWords,
  formatBiValue,
} from '@euler/model/bi/format';
import { BiKpiViz } from '@euler/model/bi/viz';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react-native';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { BiVizMismatch } from './BiVizMismatch';
import { biWindowCaption } from './functions/biCardSubtitle';
import { BiKpiReading, biKpiReading } from './functions/biKpiReading';
import { biText } from './functions/biText';
import { biVizLook } from './functions/biVizLook';

/**
 * A text's width in ems at bold weight, roughly: Latin digits and signs are
 * about two thirds of an em, CJK (`万`, `亿`) and full-width signs (`¥`)
 * a whole one.
 */
export function biTextEms(text: string): number {
  let ems = 0;
  for (const char of text) ems += char.charCodeAt(0) < 0x80 ? 0.66 : 1;
  return ems;
}

/**
 * One number, and how it moved against the previous period. The number
 * shrinks to fit a narrow card rather than being cut off.
 */
export const BiKpiView = memo(
  ({
    viz,
    result,
    compact,
  }: {
    viz: BiKpiViz;
    result: BiDraftResult;
    /** No label and no box: the card's title already names the number. */
    compact?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    const reading = biKpiReading(
      result,
      viz.value,
      viz.format,
      viz.compare,
      biVizLook(viz).kpi,
    );
    if (!reading) return <BiVizMismatch />;
    const { ref, previous, text } = reading;
    const previousWindow = result.previous
      ? biWindowCaption(result.previous)
      : null;
    const base = rpx(compact ? 24 : 28);
    const size = width
      ? Math.max(rpx(14), Math.min(base, width / biTextEms(text)))
      : base;
    return (
      <View
        style={
          compact
            ? { gap: rpx(4) }
            : {
                gap: rpx(6),
                padding: rpx(14),
                borderRadius: rpx(10),
                backgroundColor: aiColors.muted,
              }
        }
      >
        {compact ? null : (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {biText(ref.column.label)}
          </Typography.Text>
        )}
        <View onLayout={event => setWidth(event.nativeEvent.layout.width)}>
          <Typography.Text
            bold
            size={size}
            color={aiColors.foreground}
            numberOfLines={1}
          >
            {text}
          </Typography.Text>
        </View>
        {previous !== undefined ? (
          <BiKpiDelta
            reading={reading}
            caption={!compact && previousWindow ? `（${previousWindow}）` : ''}
          />
        ) : null}
      </View>
    );
  },
);

/** How a number moved against the previous period, and what it was. */
export const BiKpiDelta = memo(
  ({
    reading,
    caption,
  }: {
    reading: BiKpiReading;
    /** After the previous value, e.g. its window. */
    caption: string;
  }) => {
    const { rpx } = useResponsiveContext();
    const { delta, good, previous, ref } = reading;
    const color =
      delta === null || delta.trend === BiDeltaTrend.Flat
        ? aiColors.mutedForeground
        : good
        ? aiColors.green
        : aiColors.red;
    const Arrow =
      delta?.trend === BiDeltaTrend.Down ? ArrowDownRight : ArrowUpRight;
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: rpx(6),
        }}
      >
        {delta !== null ? (
          <View
            accessible
            accessibilityLabel={`较上期${biDeltaWords(delta)}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(2),
              paddingHorizontal: rpx(6),
              paddingVertical: rpx(2),
              borderRadius: rpx(8),
              backgroundColor: tint(color, 0.12),
            }}
          >
            {delta.trend !== BiDeltaTrend.Flat ? (
              <Arrow size={12} color={color} />
            ) : null}
            <Typography.Text medium size={rpx(11)} color={color}>
              {delta.text}
            </Typography.Text>
          </View>
        ) : null}
        <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
          上期 {formatBiValue(previous ?? null, ref.format)}
          {caption}
        </Typography.Text>
      </View>
    );
  },
);
